import { AxiosError, AxiosResponse } from "axios";
import { IGetListRequest, IResponse, IServerError } from "../models/common";
import {
  IWCBAllProvincesCode,
  IWCBAllProvincesCodeListPayload,
  IWCBAllProvincesCodeResponse,
} from "../models";
import {
  IWCBAllProvincesRemittance,
  IWCBAllProvincesRemittanceListPayload,
  IWCBAllProvincesRemittanceResponse,
} from "../models";

import { format422Errors } from "../common/Common";
import http from "../common/http-commons";

export const WCBService = {
  // ####################################### ALL PROVINCES CODES SETUP APIS ###################################
  getAllProvincesCodes: async (
    wcbProvinceSlug: string,
    provinceId: string | null,
    clientId: string,
    taxYear: number,
    navigateToLogout: (path: string) => void,
  ): Promise<
    | IWCBAllProvincesCodeListPayload
    | IServerError
    | IResponse
    | null
    | IWCBAllProvincesCodeResponse[]
  > => {
    if (!provinceId) return null;
    try {
      const response: AxiosResponse<IWCBAllProvincesCodeListPayload> =
        await http.get<IWCBAllProvincesCodeListPayload>(
          `/v1/${wcbProvinceSlug === "ontario" ? "wsib" : "wcb"}-${
            wcbProvinceSlug === "british_columbia"
              ? "british-columbia"
              : wcbProvinceSlug === "new_brunswick"
                ? "new-brunswick"
                : wcbProvinceSlug === "newfoundland_and_labrador"
                  ? "newfoundland-and-labrador"
                  : wcbProvinceSlug === "northwest_territories"
                    ? "northwest-territories"
                    : wcbProvinceSlug === "nova_scotia"
                      ? "nova-scotia"
                      : wcbProvinceSlug
          }-code-setup/?client_id=${clientId}&tax_year=${taxYear}`,
        );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getAllProvincesCodeById: async (
    wcbProvinceSlug: string,
    provinceId: string | null,
    clientId: string,
    id: string | undefined,
    navigateToLogout: (path: string) => void,
  ): Promise<IWCBAllProvincesCode | IServerError | IResponse | null | any> => {
    if (!id) return null;
    try {
      const response: AxiosResponse<IWCBAllProvincesCode> =
        await http.get<IWCBAllProvincesCode>(
          `/v1/${wcbProvinceSlug === "ontario" ? "wsib" : "wcb"}-${
            wcbProvinceSlug === "british_columbia"
              ? "british-columbia"
              : wcbProvinceSlug === "new_brunswick"
                ? "new-brunswick"
                : wcbProvinceSlug === "newfoundland_and_labrador"
                  ? "newfoundland-and-labrador"
                  : wcbProvinceSlug === "northwest_territories"
                    ? "northwest-territories"
                    : wcbProvinceSlug === "nova_scotia"
                      ? "nova-scotia"
                      : wcbProvinceSlug
          }-code-setup/${id}/`,
        );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  createAllProvincesCodes: async (
    requestPayload: any,
  ): Promise<IWCBAllProvincesCode | IServerError | IResponse | any> => {
    if (requestPayload.id) {
      try {
        const response: AxiosResponse<IWCBAllProvincesCode> =
          await http.put<IWCBAllProvincesCode>(
            `/v1/${requestPayload.wcbProvinceSlug === "ontario" ? "wsib" : "wcb"}-${
              requestPayload.wcbProvinceSlug === "british_columbia"
                ? "british-columbia"
                : requestPayload.wcbProvinceSlug === "new_brunswick"
                  ? "new-brunswick"
                  : requestPayload.wcbProvinceSlug ===
                      "newfoundland_and_labrador"
                    ? "newfoundland-and-labrador"
                    : requestPayload.wcbProvinceSlug === "northwest_territories"
                      ? "northwest-territories"
                      : requestPayload.wcbProvinceSlug === "nova_scotia"
                        ? "nova-scotia"
                        : requestPayload.wcbProvinceSlug
            }-code-setup/${requestPayload.id}/`,
            requestPayload,
          );

        if (response.status === 200) {
          return response;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        if (axiosError && axiosError.status === 401) {
          requestPayload.navigateToLogout("/logout");
          return {
            status: false,
            message: "UnAuthrized",
          };
        } else {
          return {
            status: false,
            message: errorMessage,
          };
        }
      }
    }

    try {
      const response: AxiosResponse<IWCBAllProvincesCode> =
        await http.post<IWCBAllProvincesCode>(
          `/v1/${requestPayload.wcbProvinceSlug === "ontario" ? "wsib" : "wcb"}-${
            requestPayload.wcbProvinceSlug === "british_columbia"
              ? "british-columbia"
              : requestPayload.wcbProvinceSlug === "new_brunswick"
                ? "new-brunswick"
                : requestPayload.wcbProvinceSlug === "newfoundland_and_labrador"
                  ? "newfoundland-and-labrador"
                  : requestPayload.wcbProvinceSlug === "northwest_territories"
                    ? "northwest-territories"
                    : requestPayload.wcbProvinceSlug === "nova_scotia"
                      ? "nova-scotia"
                      : requestPayload.wcbProvinceSlug
          }-code-setup/add-wsib-code`,
          requestPayload,
        );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  delAllProvincesCodes: async (
    delPayload: any,
  ): Promise<string | IServerError | IResponse | any> => {
    // return new Promise(resolve => setTimeout(() => resolve("Flex Field has been deleted successfully"), 1000));
    try {
      const response: AxiosResponse<string> = await http.delete<string>(
        `/v1/${delPayload.wcbProvinceSlug === "ontario" ? "wsib" : "wcb"}-${
          delPayload.wcbProvinceSlug === "british_columbia"
            ? "british-columbia"
            : delPayload.wcbProvinceSlug === "new_brunswick"
              ? "new-brunswick"
              : delPayload.wcbProvinceSlug === "newfoundland_and_labrador"
                ? "newfoundland-and-labrador"
                : delPayload.wcbProvinceSlug === "northwest_territories"
                  ? "northwest-territories"
                  : delPayload.wcbProvinceSlug === "nova_scotia"
                    ? "nova-scotia"
                    : delPayload.wcbProvinceSlug
        }-code-setup/${delPayload.codeId}/`,
      );

      if (response.status === 204) {
        return "Codes has been deleted successfully";
      } else if (response.status === 200) {
        return "Codes has been deleted successfully";
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        delPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  delBulkAllProvincesCodes: async (
    requestPayload: any,
  ): Promise<string | IServerError | IResponse> => {
    try {
      const response = await http.delete<string>(`/v1/codes/`, {
        data: requestPayload.codesIds,
      });
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getAllProvincesCodesDDL: async (
    wcbProvinceSlug: string,
    provinceId: any,
    taxYear: number,
  ): Promise<any | IServerError | IResponse | null | any> => {
    if (!provinceId) return null;
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/${wcbProvinceSlug === "ontario" ? "wsib" : "wcb"}-${
          wcbProvinceSlug === "british_columbia"
            ? "british-columbia"
            : wcbProvinceSlug === "new_brunswick"
              ? "new-brunswick"
              : wcbProvinceSlug === "newfoundland_and_labrador"
                ? "newfoundland-and-labrador"
                : wcbProvinceSlug === "northwest_territories"
                  ? "northwest-territories"
                  : wcbProvinceSlug === "nova_scotia"
                    ? "nova-scotia"
                    : wcbProvinceSlug
        }-code-setup/wsib-ontario-codes-ddl?province_id=${provinceId}&tax_year=${taxYear}`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 404) {
        return [];
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 404) {
        return [];
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // ####################################### ALL PROVINCES Remittance SETUP APIS ###################################

  getWCBAllProvincesRemittance: async (
    wcbProvinceSlug: string,
    remittanceFrequencyId: string | undefined,
    clientId: string,
    taxYear: number,
    loadedProfileId: string,
    navigateToLogout: (path: string) => void,
  ): Promise<
    | IWCBAllProvincesRemittanceListPayload
    | IServerError
    | IResponse
    | IWCBAllProvincesRemittanceResponse[]
    | null
  > => {
    if (!wcbProvinceSlug) return null;
    if (!remittanceFrequencyId) return null;
    try {
      const response: AxiosResponse<IWCBAllProvincesRemittanceListPayload> =
        await http.get<IWCBAllProvincesRemittanceListPayload>(
          "/v1/wcb-" +
            `${
              wcbProvinceSlug === "british_columbia"
                ? "british-columbia"
                : wcbProvinceSlug === "new_brunswick"
                  ? "new-brunswick"
                  : wcbProvinceSlug === "newfoundland_and_labrador"
                    ? "newfoundland-and-labrador"
                    : wcbProvinceSlug === "northwest_territories"
                      ? "northwest-territories"
                      : wcbProvinceSlug === "nova_scotia"
                        ? "nova-scotia"
                        : wcbProvinceSlug
            }` +
            "-remittance/?client_id=" +
            clientId +
            "&remittance_frequency_id=" +
            remittanceFrequencyId +
            "&tax_year=" +
            taxYear +
            "&wsib_profile_id=" +
            loadedProfileId,
        );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 201) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  getWCBAllProvincesRemittanceSavedFrequency: async (
    wcbProvinceSlug: string,
    clientId: string,
    taxYear: number,
    navigateToLogout: (path: string) => void,
  ): Promise<
    | IWCBAllProvincesRemittanceListPayload
    | IServerError
    | IResponse
    | IWCBAllProvincesRemittanceResponse[]
    | null
  > => {
    if (!wcbProvinceSlug) return null;
    try {
      const response: AxiosResponse<IWCBAllProvincesRemittanceListPayload> =
        await http.get<IWCBAllProvincesRemittanceListPayload>(
          "/v1/wcb-" +
            `${
              wcbProvinceSlug === "british_columbia"
                ? "british-columbia"
                : wcbProvinceSlug === "new_brunswick"
                  ? "new-brunswick"
                  : wcbProvinceSlug === "newfoundland_and_labrador"
                    ? "newfoundland-and-labrador"
                    : wcbProvinceSlug === "northwest_territories"
                      ? "northwest-territories"
                      : wcbProvinceSlug === "nova_scotia"
                        ? "nova-scotia"
                        : wcbProvinceSlug
            }` +
            "-remittance/" +
            clientId +
            "/?tax_year=" +
            taxYear,
        );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 201) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  createWCBAllProvincesRemittance: async (
    requestPayload: any,
  ): Promise<IWCBAllProvincesRemittance | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IWCBAllProvincesRemittance> =
        await http.put<IWCBAllProvincesRemittance>(
          `/v1/wcb-${
            requestPayload.wcbProvinceSlug === "british_columbia"
              ? "british-columbia"
              : requestPayload.wcbProvinceSlug === "new_brunswick"
                ? "new-brunswick"
                : requestPayload.wcbProvinceSlug === "newfoundland_and_labrador"
                  ? "newfoundland-and-labrador"
                  : requestPayload.wcbProvinceSlug === "northwest_territories"
                    ? "northwest-territories"
                    : requestPayload.wcbProvinceSlug === "nova_scotia"
                      ? "nova-scotia"
                      : requestPayload.wcbProvinceSlug
          }-remittance/`,
          requestPayload.data,
        );

      if (response.status === 200) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  createDefaultCode: async (
    requestPayload: any,
  ): Promise<IWCBAllProvincesRemittance | IServerError | IResponse | any> => {
    if (requestPayload.id) {
      try {
        const response: AxiosResponse<any> = await http.put<any>(
          `/v1/wsib-code-profile/${requestPayload.id}/`,
          requestPayload,
        );

        if (response.status === 200) {
          return response;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 404) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        if (axiosError && axiosError.status === 401) {
          requestPayload.navigateToLogout("/logout");
          return {
            status: false,
            message: "UnAuthrized",
          };
        } else if (axiosError && axiosError.status === 422) {
          return {
            status: false,
            message: `Errors : \n${format422Errors(
              axiosError?.response?.data,
            )}`,
          };
        } else {
          return {
            status: false,
            message: errorMessage,
          };
        }
      }
    }

    try {
      const response: AxiosResponse<IWCBAllProvincesRemittance> =
        await http.post<IWCBAllProvincesRemittance>(
          "/v1/wsib-code-profile/",
          requestPayload,
        );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  delWCBAllProvincesRemittance: async (
    delPayload: any,
  ): Promise<string | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IWCBAllProvincesRemittance> =
        await http.delete<IWCBAllProvincesRemittance>(`/v1/${delPayload.id}/`);

      if (response.status === 204) {
        return "Rate has been deleted successfully.";
      } else if (response.status === 200) {
        return "Rate has been deleted successfully.";
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        delPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getRemittanceFrequencyDDL: async (
    provinceId: string | null,
  ): Promise<any | IServerError | IResponse | null | any> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/remittance-frequency/remittance-frequency-ddl`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 404) {
        return [];
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 404) {
        return [];
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getSavedWCBAllProvincesDDL: async (
    clientId: string,
    provinceId: string | null,
  ): Promise<any | IServerError | IResponse | null | any> => {
    if (!provinceId) return null;
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/wsib-code-profile/wsib-code-profile-ddl?client_id=${clientId}&province_id=${provinceId}`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 404) {
        return [];
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 404) {
        return [];
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getDefaultCodeProfileDataById: async (
    // provinceId: string | null,
    id: null | string,
  ): Promise<any | IServerError | IResponse | null | any> => {
    if (!id) return null;
    if (id === "new-rate") return null;
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/wsib-code-profile/${id}/`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 404) {
        return [];
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 404) {
        return [];
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  createWCBAllProvincesRemittanceFrequency: async (
    wcbProvinceSlug: string,
    remittanceFrequencyId: string | undefined,
    clientId: string,
    taxYear: number,
    loadedProfileId: string,
    navigateToLogout: (path: string) => void,
  ): Promise<IWCBAllProvincesRemittance | IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse<IWCBAllProvincesRemittance> =
        await http.post<IWCBAllProvincesRemittance>(
          `/v1/wcb-${
            wcbProvinceSlug === "british_columbia"
              ? "british-columbia"
              : wcbProvinceSlug === "new_brunswick"
                ? "new-brunswick"
                : wcbProvinceSlug === "newfoundland_and_labrador"
                  ? "newfoundland-and-labrador"
                  : wcbProvinceSlug === "northwest_territories"
                    ? "northwest-territories"
                    : wcbProvinceSlug === "nova_scotia"
                      ? "nova-scotia"
                      : wcbProvinceSlug
          }-remittance/?client_id=${clientId}&remittance_frequency_id=${remittanceFrequencyId}&tax_year=${taxYear}&wsib_profile_id=${loadedProfileId}`,
        );

      if (response.status === 200) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 401) {
        // window.location.href = '/logout';
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else if (axiosError && axiosError.status === 422) {
        return {
          status: false,
          message: `Errors : \n${format422Errors(axiosError?.response?.data)}`,
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
  getDefaultCodeDDL: async (
    wcbProvinceSlug: any,
    clientId: any,
    provinceId: any,
    taxYear: number,
  ): Promise<any | IServerError | IResponse | null | any> => {
    if (!provinceId) return null;
    if (!wcbProvinceSlug) return null;
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        `/v1/wcb-${
          wcbProvinceSlug === "british_columbia"
            ? "british-columbia"
            : wcbProvinceSlug === "new_brunswick"
              ? "new-brunswick"
              : wcbProvinceSlug === "newfoundland_and_labrador"
                ? "newfoundland-and-labrador"
                : wcbProvinceSlug === "northwest_territories"
                  ? "northwest-territories"
                  : wcbProvinceSlug === "nova_scotia"
                    ? "nova-scotia"
                    : wcbProvinceSlug
        }-code-setup/company-ddl/${clientId}?province_id=${provinceId}&tax_year=${taxYear}`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 404) {
        return [];
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 404) {
        return [];
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  exportWCBRemittanceExcelReport: async (
    wcbProvinceSlug: string,
    clientId: string,
    remittanceFrequencyId: string,
    taxYear: number,
    loadedProfileId: string,
  ): Promise<any | IServerError | IResponse | any> => {
    console.log("wcbProvinceSlugwcbProvinceSlug", wcbProvinceSlug);
    const matchPovinceSlug =
      wcbProvinceSlug === "british_columbia"
        ? "british-columbia"
        : wcbProvinceSlug === "new_brunswick"
          ? "new-brunswick"
          : wcbProvinceSlug === "newfoundland_and_labrador"
            ? "newfoundland-and-labrador"
            : wcbProvinceSlug === "northwest_territories"
              ? "northwest-territories"
              : wcbProvinceSlug === "nova_scotia"
                ? "nova-scotia"
                : wcbProvinceSlug;

    const ontarioWsibSlug = wcbProvinceSlug === "ontario" ? "wsib" : "wcb";

    const url =
      `/v1/all-remittance-printing/${ontarioWsibSlug}-${matchPovinceSlug}-remittance-xlsx?client_id=` +
      clientId +
      "&remittance_frequency_id=" +
      remittanceFrequencyId +
      "&tax_year=" +
      taxYear +
      "&wsib_profile_id=" +
      loadedProfileId;

    const response: any = await http.get(url, {
      responseType: "arraybuffer",
    });

    if (response.status === 201) {
      return response.data;
    } else if (response.status === 200) {
      return response.data;
    } else if (response.status === 400) {
      const text = new TextDecoder().decode(response.data);
      const responseObject = JSON.parse(text);
      let response400 = {
        ...response,
        data: responseObject,
      };
      return response400;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }
    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
};
